import React, { useContext, useMemo, useState } from 'react'
import { Box, Button, Dialog } from '@mui/material'

import LanguageContext from '../../../contexts/LanguageContext'
import { useTexte } from '../../../contexts/FormeAdresseContext'
import closeIcon from '../../../images/closeIcon.svg'
import { STYLE_BOUTON_CERTIFICAT } from '../Certificat/FenetreCertificat'
import { mesureurCanevas } from '../Certificat/mesure'

import ApercuBilan, { usePolicePrete } from './ApercuBilan'
import { composerBilan, composerPartage, composerEmployeur } from './modeleBilan'
import { textePostLinkedIn, competencesProfil } from './partageBilan'
import { telechargerBilanPdf, telechargerPdfEmployeur } from './pdfBilan'
import { telechargerImagePartage } from './imageBilan'

/* ⛔ « VOIR MON BILAN » (07/10/2026, v3, decision de Nawal ; etape 2 :
   ouverte par la ligne « Mon bilan soft skills » du tableau de bord).

   TROIS VERSIONS, trois onglets :
     - POUR LINKEDIN : l'image a partager (vitrine 4:5), le texte du post pret
       a copier, les 3 competences pour la rubrique du profil ;
     - POUR MOI : le PDF de deux pages (vitrine puis « Prépare ton
       entretien »), la SEULE version qui parle de ce qui reste a travailler ;
     - POUR UN EMPLOYEUR : une page sobre, a la troisieme personne.
   Rien n'est envoye au serveur : tout se fabrique dans le navigateur (PDF
   par jsPDF, image par un canevas, Bilan/imageBilan.js).
   Clavier : les onglets et les boutons sont de vrais boutons ; la
   confirmation de copie est annoncee (aria-live). Tu ou vous : `useTexte`. */

const STYLE_BOUTON_PRINCIPAL = {
  fontSize: '16px !important',
  lineHeight: '26px !important',
  letterSpacing: '0px',
  height: ['45px !important', '50px !important'],
  minHeight: ['45px !important', '50px !important'],
  paddingLeft: '24px',
  paddingRight: '24px',
  whiteSpace: 'nowrap',
  textTransform: 'none',
  borderRadius: '0px',
  boxShadow: 'none',
}

const ONGLETS = ['linkedin', 'moi', 'employeur']

const BoutonPrincipal = ({ onClick, children, enCours, ...autres }) => (
  <Button type="button" variant="contained" disableElevation disableRipple disabled={enCours} onClick={onClick} sx={STYLE_BOUTON_PRINCIPAL} {...autres}>
    {children}
  </Button>
)

const FenetreBilan = ({
  ouverte, onFermer, prenomNom, nomParcours, contenu, exemple = false,
  ongletInitial = 'linkedin', emplacementQr = false, dateDelivrance,
}) => {
  const { actLanguage, languageValues } = useContext(LanguageContext)
  const t = useTexte()
  const langue = actLanguage === 'en' ? 'en' : 'fr'
  const L = (cle) => t(languageValues?.bilanSoftSkills?.[cle])
  const [onglet, setOnglet] = useState(ONGLETS.includes(ongletInitial) ? ongletInitial : 'linkedin')
  const [copie, setCopie] = useState(false)
  const [enCours, setEnCours] = useState(false)
  const pret = usePolicePrete()
  const date = useMemo(() => dateDelivrance || new Date(), [dateDelivrance])
  const donnees = { prenomNom, nomParcours, contenu, langue, exemple, dateDelivrance: date, emplacementQr }

  const pages = useMemo(() => {
    if (!pret || !contenu) return null
    const mesurer = mesureurCanevas()
    return {
      partage: composerPartage({ ...donnees, mesurer }),
      bilan: composerBilan({ ...donnees, mesurer }),
      employeur: composerEmployeur({ ...donnees, mesurer }),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pret, contenu, prenomNom, nomParcours, langue, exemple, emplacementQr])

  const post = contenu ? textePostLinkedIn({ nomParcours, contenu, langue }) : ''

  const copier = async () => {
    try {
      await navigator.clipboard.writeText(post)
      setCopie(true)
      setTimeout(() => setCopie(false), 3000)
    } catch (e) { console.error('bilan, texte non copié', e) }
  }
  const telecharger = async (quoi) => {
    if (enCours) return
    setEnCours(true)
    try {
      if (quoi === 'image') await telechargerImagePartage(donnees)
      if (quoi === 'moi') await telechargerBilanPdf(donnees)
      if (quoi === 'employeur') await telechargerPdfEmployeur(donnees)
    } catch (e) { console.error('bilan, document non fabriqué', e) }
    setEnCours(false)
  }

  const titreOnglet = { linkedin: L('ongletLinkedIn'), moi: L('ongletMoi'), employeur: L('ongletEmployeur') }
  const aide = { linkedin: L('aideLinkedIn'), moi: L('aideMoi'), employeur: L('aideEmployeur') }

  const hauteurApercu = 'calc(100vh - 330px)'

  return (
    <Dialog
      open={!!ouverte}
      onClose={onFermer}
      maxWidth="lg"
      fullWidth
      aria-label={L('ligneTitre')}
      PaperProps={{
        'data-fenetre-bilan': true,
        sx: {
          borderRadius: '0px',
          margin: ['12px', '32px'],
          width: ['calc(100% - 24px)', 'calc(100% - 64px)'],
          maxHeight: ['calc(100% - 24px)', 'calc(100% - 64px)'],
          backgroundColor: '#FFFFFF',
        },
      }}
    >
      <Box sx={{ position: 'relative', padding: ['18px 16px 20px', '24px 28px 28px', '28px 36px 32px', '28px 36px 32px'] }}>
        {/* en-tete : titre et fermeture */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', paddingRight: '36px' }}>
          <Box sx={{ color: 'text.black', fontSize: ['22px', '26px', '28px', '28px'], lineHeight: 1.2, letterSpacing: '-0.02em' }}>
            {L('ligneTitre')}
          </Box>
        </Box>
        <Box
          component="button"
          type="button"
          onClick={onFermer}
          aria-label={languageValues?.close?.[actLanguage]}
          sx={{
            position: 'absolute', top: ['14px', '20px'], right: ['10px', '20px'],
            width: '36px', height: '36px', padding: '9px', margin: 0,
            border: 0, background: 'transparent', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <img src={closeIcon} alt="" style={{ width: '18px', height: '18px', display: 'block' }} />
        </Box>

        {/* les trois onglets */}
        <Box role="tablist" sx={{ display: 'flex', marginTop: ['14px', '20px'], borderBottom: '1px solid #E8E1D6' }}>
          {ONGLETS.map((o) => (
            <Box
              key={o}
              component="button"
              type="button"
              role="tab"
              aria-selected={onglet === o}
              data-onglet-bilan={o}
              onClick={() => setOnglet(o)}
              sx={{
                flex: ['1 1 0', '0 0 auto'],
                padding: ['10px 4px 12px', '12px 22px 14px'],
                margin: 0,
                marginBottom: '-1px',
                border: 0,
                borderBottom: onglet === o ? '2px solid #0A0A0B' : '2px solid transparent',
                background: 'transparent',
                cursor: 'pointer',
                fontFamily: 'Questrial',
                fontSize: ['14px', '16px', '16px', '17px'],
                lineHeight: 1.3,
                color: onglet === o ? '#0A0A0B' : '#8A8480',
                whiteSpace: 'nowrap',
              }}
            >
              {titreOnglet[o]}
            </Box>
          ))}
        </Box>

        {/* confirmation discrete de la copie, annoncee aux lecteurs d'ecran */}
        <Box role="status" aria-live="polite" data-post-copie={copie ? 'oui' : 'non'} sx={{
          position: 'absolute', top: ['18px', '28px'], right: ['56px', '70px'],
          fontSize: '14px', color: '#5A544D', opacity: copie ? 1 : 0, transition: 'opacity 0.2s',
        }}>
          {copie ? L('postCopie') : ''}
        </Box>

        {/* une phrase d'aide et les boutons de l'onglet */}
        <Box sx={{
          display: 'flex', flexDirection: ['column', 'row'], alignItems: ['stretch', 'center'], justifyContent: 'space-between',
          gap: ['12px', '20px'], marginTop: ['16px', '22px'],
        }}>
          <Box sx={{ color: 'text.subTitle', fontSize: ['15px', '16px'], lineHeight: 1.45 }}>{aide[onglet]}</Box>
          <Box sx={{ display: 'flex', flexDirection: ['column', 'row'], gap: ['10px', '12px'], flexShrink: 0 }}>
            {onglet === 'linkedin' && <>
              <Button type="button" variant="outlined" disableRipple data-copier-post onClick={copier} sx={{ ...STYLE_BOUTON_CERTIFICAT }}>
                {L('copierPost')}
              </Button>
              <BoutonPrincipal enCours={enCours} data-telecharger-image onClick={() => telecharger('image')}>{L('telechargerImage')}</BoutonPrincipal>
            </>}
            {onglet !== 'linkedin' &&
              <BoutonPrincipal enCours={enCours} data-telecharger-pdf={onglet} onClick={() => telecharger(onglet)}>{L('telechargerPdf')}</BoutonPrincipal>
            }
          </Box>
        </Box>

        {/* les apercus */}
        <Box role="tabpanel" aria-label={titreOnglet[onglet]} sx={{ marginTop: ['16px', '22px'] }}>
          {onglet === 'linkedin' &&
            <Box sx={{ display: 'flex', flexDirection: ['column', 'column', 'row', 'row'], gap: ['16px', '24px', '32px', '32px'], alignItems: 'flex-start' }}>
              <Box sx={{ width: ['100%', `min(100%, calc(${hauteurApercu} * 0.8))`, `min(48%, calc(${hauteurApercu} * 0.8))`, `min(48%, calc(${hauteurApercu} * 0.8))`], flexShrink: 0 }}>
                <ApercuBilan page={pages?.partage} libelle={L('ongletLinkedIn')} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <Box sx={{ color: '#E26438', fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '8px' }}>{L('texteDuPost')}</Box>
                <Box data-texte-post sx={{
                  background: '#FFFCF8', border: '1px solid #E8E1D6', padding: ['14px', '18px'],
                  color: '#2A2520', fontSize: ['15px', '16px'], lineHeight: 1.55, whiteSpace: 'pre-wrap',
                }}>
                  {post}
                </Box>
                <Box sx={{ color: 'text.subTitle', fontSize: ['14px', '15px'], lineHeight: 1.5, marginTop: '18px' }}>
                  {L('competencesProfil')}
                  {competencesProfil(contenu).map((c) => <Box key={c} sx={{ color: '#0A0A0B' }}>{c}</Box>)}
                </Box>
              </Box>
            </Box>
          }
          {onglet === 'moi' &&
            <Box sx={{ display: 'flex', flexDirection: ['column', 'row'], gap: ['16px', '20px'], justifyContent: 'center' }}>
              {(pages?.bilan?.pages || []).map((p, i) => (
                <Box key={i} sx={{ width: ['100%', `min(49%, calc(${hauteurApercu} * 210 / 297))`] }}>
                  <ApercuBilan page={p} libelle={L('ongletMoi') + ' ' + (i + 1)} />
                </Box>
              ))}
            </Box>
          }
          {onglet === 'employeur' &&
            <Box sx={{ width: ['100%', `min(100%, calc(${hauteurApercu} * 210 / 297))`], margin: '0 auto' }}>
              <ApercuBilan page={pages?.employeur} libelle={L('ongletEmployeur')} />
            </Box>
          }
        </Box>
      </Box>
    </Dialog>
  )
}

export default FenetreBilan
